import { useEffect, useRef, useState } from 'react';
import { fetchAttachmentUrl } from '@/api/reports';
import { Skeleton } from '@/components/ui/skeleton';
import type { ReportAttachment } from '@/types/reports';

interface ReportMediaProps {
  reportId: string;
  attachment: ReportAttachment;
}

export function ReportMedia({ reportId, attachment }: ReportMediaProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const retriedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setError(false);
    retriedRef.current = false;
    fetchAttachmentUrl(reportId, attachment.id)
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, attachment.id]);

  // SPEC-018: presigned URLs expire after ~5 minutes. If the media tag fails to
  // load — most likely because the URL TTL elapsed while the user lingered on
  // the page — refetch a fresh URL exactly once before giving up.
  function handleMediaError() {
    if (retriedRef.current) {
      setError(true);
      return;
    }
    retriedRef.current = true;
    fetchAttachmentUrl(reportId, attachment.id)
      .then((res) => setUrl(res.url))
      .catch(() => setError(true));
  }

  if (error) {
    return (
      <div className="flex h-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
        Failed to load media
      </div>
    );
  }

  if (!url) {
    return <Skeleton className="h-48 w-full rounded-md" />;
  }

  if (attachment.type === 'photo') {
    return (
      <img
        src={url}
        alt="Report attachment"
        onError={handleMediaError}
        className="max-h-96 w-full rounded-md object-contain"
      />
    );
  }

  return (
    <video
      src={url}
      controls
      preload="metadata"
      onError={handleMediaError}
      className="max-h-96 w-full rounded-md"
    />
  );
}
